import { FC, useId } from 'react';

import { entityTeamAssociateType, EntityTeamItem } from '@/api/entity/types';
import ChevronDownIcon from '@/assets/svg/ui-elements/chevron-down.svg';
import {
  Badge,
  ExpandToggle,
  type TableColumn,
  type TableRow,
  Tooltip,
  Typography,
  VisuallyHidden,
} from '@/shared/design-system';

import { MemberActions } from './member-actions';
import { MemberDirectorCell } from './member-director-cell';
import { TEAM_STATUS_CONFIG } from './status-config';
import { NameCell, StatusCell, StatusTooltipTrigger } from './user-details.styled';

const TOOLTIP_MIN_LENGTH = 28;

export type MemberRow = TableRow & {
  member: EntityTeamItem;
  isOpen: boolean;
  isLastAdmin: boolean;
  onToggle: () => void;
};

export const memberDisplayName = ({ name, alias }: EntityTeamItem) =>
  name && alias ? `${name} (${alias})` : name || alias || '';

export const countMemberFunds = (member: EntityTeamItem) =>
  member.fundAssociations?.filter(
    (fund) =>
      fund.type === entityTeamAssociateType.Member ||
      fund.type === entityTeamAssociateType.FundInvitation,
  )?.length || 0;

const MemberNameCell: FC<{ row: MemberRow }> = ({ row }) => {
  const displayName = memberDisplayName(row.member);

  return (
    <NameCell>
      <ExpandToggle
        aria-label={`Show funds for ${displayName}`}
        aria-expanded={row.isOpen}
        disabled={!row.member.fundAssociations.length}
        onClick={(event) => {
          event.stopPropagation();
          row.onToggle();
        }}
      >
        <ChevronDownIcon style={{ transform: row.isOpen ? 'rotate(180deg)' : undefined }} />
      </ExpandToggle>
      <Tooltip title={displayName} disabled={displayName.length < TOOLTIP_MIN_LENGTH} side="top">
        <Typography variant="body3" noWrap>
          {displayName || '-'}
        </Typography>
      </Tooltip>
    </NameCell>
  );
};

const MemberStatusCell: FC<{ member: EntityTeamItem }> = ({ member }) => {
  const descriptionId = `member-status-hint-${useId()}`;
  const config = TEAM_STATUS_CONFIG[member.status];

  return (
    <StatusCell>
      <Tooltip title={config.tooltip} side="top">
        <StatusTooltipTrigger role="term" tabIndex={0} aria-describedby={descriptionId}>
          <Badge size="sm" variant="subtle" color={config.color} label={config.label} />
        </StatusTooltipTrigger>
      </Tooltip>
      <VisuallyHidden id={descriptionId}>{config.tooltip}</VisuallyHidden>
    </StatusCell>
  );
};

export const buildMemberColumns = ({
  currentUserId,
  entityId,
}: {
  currentUserId: string;
  entityId?: string | null;
}): TableColumn<MemberRow>[] => [
  {
    key: 'name',
    header: 'Name',
    width: '1.9fr',
    minWidth: 200,
    cell: (row) => <MemberNameCell row={row} />,
  },
  {
    key: 'email',
    header: 'Email address',
    width: '2.2fr',
    minWidth: 200,
    cell: (row) => (
      <Tooltip
        title={row.member.email}
        disabled={row.member.email.length < TOOLTIP_MIN_LENGTH}
        side="top"
      >
        <Typography variant="body3" noWrap>
          {row.member.email}
        </Typography>
      </Tooltip>
    ),
  },
  {
    key: 'funds',
    header: '# Funds',
    align: 'center',
    width: 90,
    cell: (row) => <Typography variant="body3">{countMemberFunds(row.member)}</Typography>,
  },
  {
    key: 'role',
    header: 'Role',
    width: 130,
    cell: (row) => <Typography variant="body3">{row.member.role}</Typography>,
  },
  {
    key: 'director',
    header: 'Director',
    align: 'center',
    width: 110,
    cell: (row) => <MemberDirectorCell data={row.member} />,
  },
  {
    key: 'status',
    header: 'Status',
    align: 'center',
    width: '1.2fr',
    minWidth: 180,
    cell: (row) => <MemberStatusCell member={row.member} />,
  },
  {
    key: 'actions',
    header: 'Actions',
    align: 'right',
    width: 160,
    cell: (row) => (
      <MemberActions
        data={row.member}
        userId={currentUserId}
        entityId={entityId}
        isLastAdmin={row.isLastAdmin}
      />
    ),
  },
];
